import {
  getStandardField,
  isEventShapedField,
  UNIVERSAL_BASE_FIELD_KEYS,
  UNIVERSAL_BASE_TERM_CLAUSE,
  type StandardFieldKey,
} from './catalog';
import {
  MODELING_FIELD_SETS,
  MODELED_OUTPUTS,
  type FieldSetOption,
  type ModeledOutput,
  type ModeledOutputSpec,
} from './modeling-readiness';
import { normalizeDate, parseDateWithFormat, type DateGrain } from './date-parse';
import type { DeclaredDateFormat } from './declared-column-facts';
import { isNullLike } from './null-like';
import { parseNumericValue } from './numeric-parse';
import { requiresEventVocabulary } from './value-classification';
import type { FileType } from './types';

export const CUSTOM_ATTRIBUTE_LEVELS = ['loan', 'observation', 'event'] as const;
export type CustomAttributeLevel = (typeof CUSTOM_ATTRIBUTE_LEVELS)[number];

export type PortfolioFieldLevel = 'identity' | 'loan' | 'observation' | 'event';

export interface CleanedCellValue {
  readonly valueText: string | null;
  readonly numericValue: string | null;
  readonly isBlankLike: boolean;
  /**
   * WS-E (F46/F53): present, non-blank, but does NOT fit the field's canonical
   * type — a numeric Standard field whose value failed the numeric parse (also
   * presents as blank), or a DATE Standard field whose value produced no YYYYMMDD
   * ordinal (display text KEPT — present, just never "have"/range-sliceable).
   * Renamed from `isNumericInvalid` when the date arm joined; drives the
   * readiness `wrong_format` status for BOTH typed families.
   */
  readonly isValueInvalid: boolean;
}

/**
 * A raw-ledger cell: target-FREE cleaning of a source value. The decouple stores
 * these at ingest (independent of the mapping) and resolves the mapping-dependent
 * shape ({@link projectCleanedCellValue}) at projection time. `numericValue` is the
 * UNCONDITIONAL numeric parse — kept for every column so type inference and numeric
 * re-typing can run later without knowing the target.
 */
export interface RawCellValue {
  readonly valueText: string | null;
  readonly numericValue: string | null;
  readonly isBlankLike: boolean;
  /**
   * Unconditional date/period parse → a `YYYYMMDD` integer ordinal, or null. Kept
   * for every column (like {@link numericValue}) so date type inference can run as a
   * pure DB aggregate over the persisted ordinal, with no mapping and no R2 re-read.
   */
  readonly dateOrdinal: number | null;
  /** The matched date grain, or null — the date control's picker-granularity hint. */
  readonly dateGrain: DateGrain | null;
}

export interface PortfolioFieldPresence {
  readonly key: StandardFieldKey;
  readonly status: 'have' | 'wrong_format';
  /** Provenance: the winning source ROW id (C6.1 row-grain ledger). */
  readonly sourceRowId?: string | undefined;
}

export interface PortfolioLoanReadinessInput {
  readonly loanId: string;
  readonly fields: readonly PortfolioFieldPresence[];
  /**
   * WS-H I2 (R2, founder-ruled 2026-07-11): the loan has NO age anchor — no
   * provided origination date and first seen in (or before) the org's earliest
   * observed month (`originationAgeBasis` ∈ {unanchored_first_month, none} —
   * common/modeling/membership.ts, the ONE shared predicate). The age-indexed
   * curves exclude such loans (`isModeled` requires ageability), so readiness
   * must too: every output reads not-ready with `origination_date` leading the
   * missing fields (the R2 unlock — a two-column loan_id + origination_date file
   * anchors them). Computed VALUE-level by the SQL composers (D3 reads the
   * projected facts + the accepted-files window start; Review computes its
   * best-known equivalent over draft ∪ active rows); absent/false = ageable.
   */
  readonly unageable?: boolean;
}

/**
 * The PORTFOLIO-LEVEL event signals: the event-shaped standard keys (see
 * `EVENT_SHAPED_FIELD_KEYS`) present anywhere in the data the readiness
 * population is judged against — the org's accepted mappings at projection (D3)
 * time, plus the upload's own included mappings at Review time. An event-shaped
 * clause key counts as satisfied for EVERY loan when its signal is in this set: a
 * loan does not need its OWN charge-off/payoff record to be ready — absence of an
 * event field on a loan means "the event has not happened", and the loan
 * contributes exposure to the curves regardless. Non-event (measurement) keys in
 * the set are ignored — only event-shaped keys ever satisfy portfolio-level.
 */
export type PortfolioSignalSet = ReadonlySet<StandardFieldKey>;

/** No portfolio-level signals — per-loan-only semantics (the default). */
export const EMPTY_PORTFOLIO_SIGNALS: PortfolioSignalSet = new Set();

/**
 * A field paired with a loan count. Used in two places, same shape: the fields a
 * winning method draws from (loans on that method that supplied the field), and the
 * fields the unready remainder needs (unready loans a field would help).
 */
export interface FieldUsageSummary {
  readonly key: StandardFieldKey;
  readonly loanCount: number;
}

/**
 * One method that WINS for ≥1 loan: its share of upload-touched loans + the fields
 * its loans actually draw from (base fields excluded; a present preferred-when-available
 * field included). Only winning methods appear — never the unused lower-priority options,
 * which is what made the old per-option field-coverage view noisy and double-counted.
 */
export interface MethodUsageSummary {
  readonly optionId: string;
  readonly labelKey: string;
  readonly loanCount: number;
  readonly percent: number;
  readonly fields: readonly FieldUsageSummary[];
}

/**
 * An upgrade nudge toward the preferred method: supply `addFields` and `loanCount`
 * loans currently on a lower-priority method move up to `toLabelKey`. `benefitKey`
 * is worded to that method's ACTUAL benefit (e.g. measuring loss directly, or reading
 * payoffs from an explicit signal) — never a blanket "more accurate".
 */
export interface MethodUpgradeSummary {
  readonly toOptionId: string;
  readonly toLabelKey: string;
  readonly benefitKey: string;
  readonly addFields: readonly StandardFieldKey[];
  readonly loanCount: number;
}

/**
 * The loans NOT ready for an output, with the fields that would make the most of them
 * ready — each unready loan counted toward its CHEAPEST path to ready (nearest option),
 * so the counts point at the highest-leverage fields to supply next.
 */
export interface ReadinessRemainder {
  readonly loanCount: number;
  readonly neededFields: readonly FieldUsageSummary[];
}

export interface LoanOutputReadiness {
  readonly output: ModeledOutput;
  readonly ready: boolean;
  readonly methodId: string | null;
  readonly labelKey: string | null;
  /**
   * Fields the winning method drew from — the clause field actually present per clause,
   * plus any present preferred-when-available field. Base fields excluded; empty when
   * the loan is not ready.
   */
  readonly usedFields: readonly StandardFieldKey[];
  readonly missingFields: readonly StandardFieldKey[];
  /** Provenance: the winning source ROW ids behind the used clause fields (C6.1). */
  readonly sourceRowIds: readonly string[];
}

export interface LoanReadinessResult {
  readonly loanId: string;
  readonly outputs: readonly LoanOutputReadiness[];
}

export interface OutputReadinessSummary {
  readonly output: ModeledOutput;
  readonly totalLoans: number;
  readonly readyLoans: number;
  readonly readyPercent: number;
  readonly missingCriticalLoans: number;
  /** Winning methods only, in preference order (preferred first). */
  readonly methods: readonly MethodUsageSummary[];
  /** At most one upgrade nudge toward the preferred method; empty when none is worthwhile. */
  readonly upgrades: readonly MethodUpgradeSummary[];
  /** The unready loans + what they need, or null when every loan is ready. */
  readonly remainder: ReadinessRemainder | null;
}

export interface RowLevelReadinessSummary {
  readonly totalLoans: number;
  readonly outputs: readonly OutputReadinessSummary[];
  readonly loans: readonly LoanReadinessResult[];
}

const LOAN_LEVEL_STANDARD_FIELDS = new Set<StandardFieldKey>([
  'origination_date',
  'original_loan_amount',
  'term_months',
  'maturity_date',
  'interest_rate',
  'fico_score',
]);

const TRANSACTION_AMOUNT_FIELD_ORDER = [
  'net_charge_off_amount',
  'charge_off_amount',
  'recovery_amount',
  'payoff_amount',
  'partial_prepayment_amount',
  'unscheduled_principal_amount',
] as const satisfies readonly StandardFieldKey[];

function percent(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

function uniqueKeys(keys: readonly StandardFieldKey[]): readonly StandardFieldKey[] {
  return [...new Set(keys)];
}

function optionRequiredKeys(option: FieldSetOption): readonly StandardFieldKey[] {
  return uniqueKeys([...UNIVERSAL_BASE_FIELD_KEYS, ...option.clauses.flat()]);
}

/**
 * The persisted `numericValue` for a raw cell: the ONE canonical numeric parse
 * ({@link parseNumericValue}), stringified, or null. Ingest, type inference, and the
 * SQL `numericValue IS NOT NULL` aggregate all read this same grammar, so they can
 * never disagree about whether a cell is a number (the drift the `fact-inference`
 * PARITY NOTE used to call out). KEEP IN SYNC with `numeric-parse`/`parsing-rules`.
 */
function normalizeNumeric(raw: string): string | null {
  const value = parseNumericValue(raw);
  return value === null ? null : String(value);
}

/**
 * Canonical identity for a CUSTOM field's source-column name, so the same field
 * MERGES across uploads despite cosmetic drift (`"FICO Score"` ≡ `"fico  score"` ≡
 * `" FICO Score "`): lowercased, trimmed, internal whitespace runs collapsed to one
 * space. Standard fields use their `StandardFieldKey` directly and never pass through
 * here. This is the durable registry key and the `portfolio_loan_attributes.attributeKey`
 * written for a custom field; the original spelling is kept separately as the display label.
 */
export function canonicalFieldKey(sourceColumnName: string): string {
  return sourceColumnName.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Reserved namespace prefix for CUSTOM field keys (F26). `:` can never appear in
 * a `StandardFieldKey` (catalog keys are snake_case identifiers), so a custom key
 * can never collide with — or silently merge into — a standard field's key in the
 * shared `portfolio_fields (org, key)` / `attributeKey` space, no matter what the
 * source column is headed (`Interest_Rate` → `c:interest_rate`, disjoint from the
 * standard `interest_rate`). KEEP IN SYNC with the inline SQL in the server
 * projection (`server/db/uploads/portfolio-model.ts` `canonicalCustomKey`).
 */
export const CUSTOM_FIELD_KEY_PREFIX = 'c:';

/**
 * The durable, NAMESPACED canonical key for a custom field: the reserved prefix +
 * {@link canonicalFieldKey} of the (possibly renamed) label. This is the registry
 * key and the `portfolio_loan_attributes.attributeKey` written for every custom
 * field; standard fields keep their bare `StandardFieldKey`.
 */
export function customCanonicalKey(label: string): string {
  return CUSTOM_FIELD_KEY_PREFIX + canonicalFieldKey(label);
}

export function isNumericPortfolioField(key: StandardFieldKey): boolean {
  return getStandardField(key).valueType === 'numeric';
}

/** Whether the Standard field's canonical value is a date ordinal (`valueType === 'date'`). */
export function isDatePortfolioField(key: StandardFieldKey): boolean {
  return getStandardField(key).valueType === 'date';
}

/**
 * Target-FREE cell cleaning for the raw ledger: trim, null-like detection, and an
 * UNCONDITIONAL numeric parse. Runs at ingest without the mapping; the
 * mapping-dependent shaping is applied later by {@link projectCleanedCellValue}.
 *
 * `dateFormat` (optional) is the column's DECLARED date-format fact (WS-E
 * instance #2): when present, the date parse is the authoritative
 * {@link parseDateWithFormat} — the ONLY place the ambiguous two-number
 * orientation is ever resolved — so a `dmy` column's `04/07/2025` reads July 4th
 * and a cell that does not fit the declared format reads NO ordinal
 * (wrong_format downstream, never guessed). Absent (the default — every existing
 * caller), the parse is the format-AGNOSTIC {@link normalizeDate}, byte-identical
 * to the pre-fact behavior.
 */
export function rawCleanCellValue(
  raw: string,
  dateFormat?: DeclaredDateFormat,
): RawCellValue {
  const trimmed = raw.trim();
  if (isNullLike(trimmed)) {
    return {
      valueText: null,
      numericValue: null,
      isBlankLike: true,
      dateOrdinal: null,
      dateGrain: null,
    };
  }
  const date =
    dateFormat === undefined
      ? normalizeDate(trimmed)
      : parseDateWithFormat(trimmed, dateFormat);
  return {
    valueText: trimmed,
    numericValue: normalizeNumeric(trimmed),
    isBlankLike: false,
    dateOrdinal: date.ordinal,
    dateGrain: date.grain,
  };
}

/**
 * The row-grain (C6.1) cell payload for ONE source row: the four per-row JSONB maps the
 * ledger stores, all keyed by raw source column name.
 *  - `cells`: raw NON-BLANK text (authoritative provenance);
 *  - `blankCells`: raw text of blank-LIKE cells ("N/A", "-", …) — real source content,
 *    semantically blank, kept for audit;
 *  - `parsedNumerics`/`parsedDates`: the type-free parse caches (keys ⊆ `cells`' keys).
 * A truly-EMPTY cell appears in NO map. So `non-blank(col) ⇔ cells ? col`.
 */
export interface RowCellPayload {
  readonly cells: Record<string, string>;
  readonly blankCells: Record<string, string>;
  readonly parsedNumerics: Record<string, number>;
  readonly parsedDates: Record<string, number>;
}

/**
 * Build a row's {@link RowCellPayload} from its raw cell text, matched to `header` by
 * INDEX. THE single row-grain assembly, shared by the production window writer
 * (`ingest-window.ts`) and the test-seed writer (`dbSeedUploadSourceRows`) so the two can
 * never disagree about how a raw row becomes JSONB — one parser ({@link rawCleanCellValue}),
 * one blank/empty classification.
 *
 * FIDELITY: `rawCleanCellValue` returns `valueText: null` for BOTH truly-empty and
 * blank-like cells, discarding the raw sentinel text. So we branch on the RAW `rawCells[j]`
 * string, not on the cleaned output: a trimmed-empty cell is absent from every map; a
 * blank-LIKE cell keeps its raw trimmed text in `blankCells` only; a non-blank cell lands
 * in `cells`, plus `parsedNumerics`/`parsedDates` exactly where the canonical parse hit.
 *
 * `dateFormats` (optional) maps raw source column NAME → the column's declared
 * date-format fact: for a column with an entry, `parsedDates[col]` is written from
 * the authoritative `parseDateWithFormat(cell, fmt)` ordinal instead of the
 * agnostic parse (the WS-E ingest coordination point — the ingest window threads
 * the per-column map from its queue message). Columns without an entry — and
 * every caller that omits the map (test seeds, golden runner, identity cleaning)
 * — keep the format-agnostic path byte-identical to before.
 */
export function buildRowCellPayload(
  header: readonly string[],
  rawCells: readonly (string | undefined)[],
  dateFormats?: Readonly<Record<string, DeclaredDateFormat>>,
): RowCellPayload {
  // Null-prototype objects: the keys are UNTRUSTED raw column names, and on a plain `{}` a
  // column literally named "__proto__" would hit Object.prototype's accessor instead of
  // creating an own property — silently dropping that column's every value. JSON.stringify
  // serializes null-prototype objects identically.
  const cells: Record<string, string> = Object.create(null) as Record<string, string>;
  const blankCells: Record<string, string> = Object.create(null) as Record<string, string>;
  const parsedNumerics: Record<string, number> = Object.create(null) as Record<string, number>;
  const parsedDates: Record<string, number> = Object.create(null) as Record<string, number>;
  header.forEach((col, j) => {
    const trimmed = (rawCells[j] ?? '').trim();
    if (trimmed === '') return; // truly-empty ⇒ absent from every map
    // Own-property lookup: the map rides a JSON queue message keyed by UNTRUSTED
    // raw column names, so a column literally named "constructor"/"__proto__"
    // must never read an inherited Object.prototype member as its format.
    const declaredFormat =
      dateFormats !== undefined && Object.prototype.hasOwnProperty.call(dateFormats, col)
        ? dateFormats[col]
        : undefined;
    const cleaned = rawCleanCellValue(trimmed, declaredFormat);
    if (cleaned.isBlankLike) {
      blankCells[col] = trimmed; // blank-LIKE: raw text kept, semantically blank
      return;
    }
    // Non-blank. Duplicate header names are NOT deduped: a later column sharing a header
    // last-wins in these maps — harmless for DRAFT data because analyze hard-rejects
    // duplicate headers with a 400 before a file can ever be accepted/projected (§3.4).
    cells[col] = trimmed;
    if (cleaned.numericValue !== null) parsedNumerics[col] = Number(cleaned.numericValue);
    if (cleaned.dateOrdinal !== null) parsedDates[col] = cleaned.dateOrdinal;
  });
  return { cells, blankCells, parsedNumerics, parsedDates };
}

/**
 * Apply a resolved target to a raw-cleaned cell, shaped by the field's canonical
 * `valueType` (numeric / date / text-family):
 *  - blank stays blank (target-independent);
 *  - a NUMERIC field keeps the parsed number, or flags `isValueInvalid` (and presents
 *    as blank) when the non-blank value did not parse;
 *  - a DATE field stores its `YYYYMMDD` ordinal in `numericValue` (the canonical sortable
 *    value, so date ranges slice on one column) with the display string in `valueText`,
 *    or flags `isValueInvalid` when the value is not a date (WS-E F46/F53: the display
 *    text is KEPT — present, never "have");
 *  - any text-family target keeps the text and carries no number.
 */
export function projectCleanedCellValue(
  raw: RawCellValue,
  standardFieldKey: StandardFieldKey | null,
): CleanedCellValue {
  if (raw.isBlankLike) {
    return { valueText: null, numericValue: null, isBlankLike: true, isValueInvalid: false };
  }
  if (standardFieldKey !== null && isNumericPortfolioField(standardFieldKey)) {
    if (raw.numericValue === null) {
      return { valueText: null, numericValue: null, isBlankLike: true, isValueInvalid: true };
    }
    return {
      valueText: raw.valueText,
      numericValue: raw.numericValue,
      isBlankLike: false,
      isValueInvalid: false,
    };
  }
  if (standardFieldKey !== null && isDatePortfolioField(standardFieldKey)) {
    // A real date gets its YYYYMMDD ordinal in numericValue (the canonical sortable
    // value, so date ranges slice on one column). A value we can't parse as a date is
    // kept as display text (present, still rendered, deliberately NOT blanked) but
    // flagged INVALID (WS-E F46/F53): the derive needs a parsed ordinal, so an
    // unparseable date must read `wrong_format`, never "have" — Review can no longer
    // promise coverage the curves exclude (`maturity_date = 'REVOLVING'`, locale
    // dates the grammar misses).
    return {
      valueText: raw.valueText,
      numericValue: raw.dateOrdinal === null ? null : String(raw.dateOrdinal),
      isBlankLike: false,
      isValueInvalid: raw.dateOrdinal === null,
    };
  }
  return { valueText: raw.valueText, numericValue: null, isBlankLike: false, isValueInvalid: false };
}

/**
 * The original ingest-time cleaning, preserved as the composition of the two halves
 * above so its callers and tests are unaffected:
 * `cleanPortfolioCellValue(s, k) === projectCleanedCellValue(rawCleanCellValue(s), k)`.
 */
export function cleanPortfolioCellValue(
  raw: string,
  standardFieldKey: StandardFieldKey | null,
): CleanedCellValue {
  return projectCleanedCellValue(rawCleanCellValue(raw), standardFieldKey);
}

export function portfolioFieldLevel(
  fileType: FileType,
  standardFieldKey: StandardFieldKey,
): PortfolioFieldLevel {
  if (standardFieldKey === 'loan_id' || standardFieldKey === 'tax_id') return 'identity';
  if (fileType === 'transaction') return 'event';
  if (LOAN_LEVEL_STANDARD_FIELDS.has(standardFieldKey)) return 'loan';
  if (fileType === 'origination') return 'loan';
  return 'observation';
}

export function customAttributeLevelForFileType(fileType: FileType): CustomAttributeLevel {
  return fileType === 'transaction' ? 'event' : 'loan';
}

/** A raw fact's (re-resolvable) mapping target: the source column and where it maps. */
export interface ResolvedFactMapping {
  readonly standardFieldKey: StandardFieldKey | null;
  readonly sourceColumnName: string;
}

/** A raw fact resolved against its target — the mapping-dependent fields ingest used
 *  to bake, now derived on read. Shared by the DB projection and the Review draft so
 *  the preview and the persisted projection can never disagree. */
export interface ResolvedFactValue {
  readonly standardFieldKey: StandardFieldKey | null;
  /** Raw source-column name for a custom field (the durable display label), or null. */
  readonly customAttributeName: string | null;
  /**
   * Canonical identity key for a custom field ({@link customCanonicalKey} of the source
   * column — the `c:`-namespaced form), or null. This — not the raw name — is the
   * `attributeKey` the projection writes, so the same field merges across uploads and
   * spelling drift, and can never collide with a standard field's key (F26).
   */
  readonly customAttributeKey: string | null;
  readonly customAttributeLevel: CustomAttributeLevel | null;
  readonly valueText: string | null;
  readonly numericValue: string | null;
  readonly isBlankLike: boolean;
  /** Present-but-does-not-fit-the-canonical-type (numeric OR date) — see
   *  {@link CleanedCellValue.isValueInvalid}. */
  readonly isValueInvalid: boolean;
}

/**
 * Resolve a raw-ledger cell against its mapping target, reproducing exactly the
 * fact fields the old ingest baked at parse time (handlers `parseUploadSourceFile`):
 * a Standard mapping keeps its key; anything else becomes a custom attribute named
 * after the source column, at the file-type's level; the value is shaped by
 * {@link projectCleanedCellValue}.
 */
export function resolvePortfolioFact(
  raw: RawCellValue,
  mapping: ResolvedFactMapping,
  fileType: FileType,
): ResolvedFactValue {
  const { standardFieldKey } = mapping;
  const customAttributeName = standardFieldKey === null ? mapping.sourceColumnName : null;
  const customAttributeKey =
    customAttributeName === null ? null : customCanonicalKey(customAttributeName);
  const customAttributeLevel =
    customAttributeName === null ? null : customAttributeLevelForFileType(fileType);
  const cleaned = projectCleanedCellValue(raw, standardFieldKey);
  return {
    standardFieldKey,
    customAttributeName,
    customAttributeKey,
    customAttributeLevel,
    valueText: cleaned.valueText,
    numericValue: cleaned.numericValue,
    isBlankLike: cleaned.isBlankLike,
    isValueInvalid: cleaned.isValueInvalid,
  };
}

export function transactionAmountField(
  values: ReadonlyMap<StandardFieldKey, { readonly numericValue: string | null }>,
): string | null {
  for (const key of TRANSACTION_AMOUNT_FIELD_ORDER) {
    const value = values.get(key)?.numericValue ?? null;
    if (value !== null) return value;
  }
  return null;
}

/**
 * A deterministic fingerprint over EVERY present transaction amount field, not
 * just the first. This is the dedupe signal when no Transaction ID is provided.
 *
 * Why every field: a single representative amount collapses genuinely distinct
 * events that happen to share a loan and date — e.g. a charge-off WITH a recovery
 * vs. one WITHOUT, on the same day — into one. Including each present amount keyed
 * by field separates them. Conversely, the SAME event re-uploaded yields the same
 * fingerprint and is correctly de-duplicated. Field order is fixed and values are
 * the normalized `numericValue`, so a re-export that only reformats the number
 * ("$10.00" → "10") still matches. Absent amount fields simply don't contribute,
 * so partial data stays supported (the explicit flexibility requirement).
 */
export function transactionAmountFingerprint(
  values: ReadonlyMap<StandardFieldKey, { readonly numericValue: string | null }>,
): string {
  const parts: string[] = [];
  for (const key of TRANSACTION_AMOUNT_FIELD_ORDER) {
    const value = values.get(key)?.numericValue ?? null;
    if (value !== null) parts.push(`${key}=${value}`);
  }
  return parts.join('|');
}

export function transactionDedupeKey(input: {
  readonly protectedLoanId: string;
  readonly transactionId: string | null;
  readonly transactionDate: string | null;
  /** Canonical fingerprint over all present amount fields ({@link transactionAmountFingerprint}). */
  readonly amountFingerprint: string | null;
}): string {
  if (input.transactionId !== null && !isNullLike(input.transactionId)) {
    return `id:${input.protectedLoanId}:${input.transactionId.trim()}`;
  }
  return `fp:${input.protectedLoanId}:${input.transactionDate ?? ''}:${input.amountFingerprint ?? ''}`;
}

function fieldMapForLoan(
  loan: PortfolioLoanReadinessInput,
): ReadonlyMap<StandardFieldKey, PortfolioFieldPresence> {
  const fields = new Map<StandardFieldKey, PortfolioFieldPresence>();
  fields.set('loan_id', { key: 'loan_id', status: 'have' });
  for (const field of loan.fields) {
    const previous = fields.get(field.key);
    if (previous?.status === 'have') continue;
    fields.set(field.key, field);
  }
  return fields;
}

function hasField(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  key: StandardFieldKey,
): boolean {
  return fields.get(key)?.status === 'have';
}

/**
 * The org's LIVE event vocabulary (WS-H F170): the classification-requiring clause
 * keys ({@link requiresEventVocabulary} — loan_status / closure_reason /
 * charge_off_status / the three flags) that carry ≥1 EVENT-MARKING classified
 * value org-wide (`EVENT_MARKING_CLASSIFICATIONS`, the derive's exact fold
 * predicate). A key in this set can produce real events in the curves; a key
 * absent from it derives NOTHING regardless of column presence.
 */
export type EventVocabularySet = ReadonlySet<StandardFieldKey>;

/**
 * No live event vocabulary — the strictly-conservative pre-accept default: an
 * unclassified status/flag value is NOT-YET-an-event (honest under-count with a
 * classify-to-unlock nudge), never a guessed event.
 */
export const EMPTY_EVENT_VOCABULARY: EventVocabularySet = new Set();

/**
 * Whether a clause key counts as satisfied for a loan: present on the loan's own
 * rows, OR — for an EVENT-SHAPED key only, when `ownFieldOnly` is not set —
 * present anywhere in the data (the portfolio-level signal). Measurement keys
 * never satisfy portfolio-level. WS-H (F170): a classification-requiring key
 * (status/flag vocabularies) satisfies NOTHING — own presence or signal — unless
 * the org's vocabulary is LIVE for it (≥1 event-marking classified value): mere
 * column presence with an all-`active` vocabulary derives no events, so it can
 * grant no readiness. `ownFieldOnly` (F55) restricts satisfaction to the loan's
 * own rows for per-loan-magnitude outputs.
 */
function hasFieldOrSignal(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  key: StandardFieldKey,
  signals: PortfolioSignalSet,
  vocabulary: EventVocabularySet,
  ownFieldOnly = false,
): boolean {
  if (requiresEventVocabulary(key) && !vocabulary.has(key)) return false;
  if (hasField(fields, key)) return true;
  return !ownFieldOnly && signals.has(key) && isEventShapedField(key);
}

function fieldMissingOrWrong(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  keys: readonly StandardFieldKey[],
  signals: PortfolioSignalSet,
  vocabulary: EventVocabularySet,
  ownFieldOnly = false,
): readonly StandardFieldKey[] {
  return keys.filter((key) => !hasFieldOrSignal(fields, key, signals, vocabulary, ownFieldOnly));
}

function optionSatisfied(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  option: FieldSetOption,
  signals: PortfolioSignalSet,
  vocabulary: EventVocabularySet,
  ownFieldOnly = false,
): boolean {
  return option.clauses.every((clause) =>
    clause.some((key) => hasFieldOrSignal(fields, key, signals, vocabulary, ownFieldOnly)),
  );
}

/** Cap on an upgrade nudge's add-set — a nudge worth more than this reads as noise. */
const UPGRADE_MAX_ADD_FIELDS = 2;

/**
 * The canonical fields a loan would need to ADD to satisfy `option` — one per
 * unsatisfied clause (the clause's first-listed field, its canonical choice). Empty
 * when the option is already satisfied. Base fields are not considered (callers use
 * this only for loans that already clear the universal base). Note (F170): a
 * vocabulary-dead clause names its canonical FIELD here even when the column is
 * mapped — the field-vocabulary is `StandardFieldKey` only; the classify-to-unlock
 * nudge is carried by the Review's parity-exclusion registry, not this list.
 */
function missingToReach(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  option: FieldSetOption,
  signals: PortfolioSignalSet,
  vocabulary: EventVocabularySet,
  ownFieldOnly = false,
): readonly StandardFieldKey[] {
  const needed: StandardFieldKey[] = [];
  for (const clause of option.clauses) {
    if (!clause.some((key) => hasFieldOrSignal(fields, key, signals, vocabulary, ownFieldOnly))) {
      const canonical = clause[0];
      if (canonical !== undefined) needed.push(canonical);
    }
  }
  return uniqueKeys(needed);
}

/**
 * For an UNREADY loan, the fields on its CHEAPEST path to ready: the universal base
 * fields when any are absent (nothing models without them), otherwise the missing
 * canonical fields of the fewest-add option. Drives the remainder's "what to supply next".
 * Signal-only options are skipped — satisfying one can never make a loan ready, so
 * pointing at its fields (a bare charge-off flag) would be false guidance.
 */
function nearestNeededFields(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  spec: ModeledOutputSpec,
  signals: PortfolioSignalSet,
  vocabulary: EventVocabularySet,
): readonly StandardFieldKey[] {
  const baseMissing = universalBaseMissing(fields);
  if (baseMissing.length > 0) return baseMissing;
  const ownFieldOnly = spec.perLoanMagnitude === true;
  let best: readonly StandardFieldKey[] | null = null;
  for (const option of spec.options) {
    if (option.signalOnly === true) continue;
    const needed = missingToReach(fields, option, signals, vocabulary, ownFieldOnly);
    if (best === null || needed.length < best.length) best = needed;
  }
  return best ?? [];
}

/**
 * The universal-base fields a loan is missing: every base key that is absent, plus —
 * when NEITHER term key is present — the term clause's canonical field
 * (`term_months`). A term-less loan can never enter the curves (its runoff would be
 * miscounted as prepayment), so readiness refuses to call it ready for any output;
 * naming `term_months` in `missingFields` makes the Review's guidance point at the
 * field that would actually bring the loan in.
 */
function universalBaseMissing(
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
): readonly StandardFieldKey[] {
  // Base + term fields are per-loan MEASUREMENTS (never event-shaped, never
  // vocabulary-gated), so the portfolio signals can never satisfy them —
  // evaluated signal-free.
  const missing = [
    ...fieldMissingOrWrong(
      fields,
      UNIVERSAL_BASE_FIELD_KEYS,
      EMPTY_PORTFOLIO_SIGNALS,
      EMPTY_EVENT_VOCABULARY,
    ),
  ];
  if (!UNIVERSAL_BASE_TERM_CLAUSE.some((key) => hasField(fields, key))) {
    const canonical = UNIVERSAL_BASE_TERM_CLAUSE[0];
    if (canonical !== undefined) missing.push(canonical);
  }
  return missing;
}

function chooseOutputForLoan(
  output: ModeledOutput,
  fields: ReadonlyMap<StandardFieldKey, PortfolioFieldPresence>,
  signals: PortfolioSignalSet,
  vocabulary: EventVocabularySet,
): LoanOutputReadiness {
  const spec = MODELING_FIELD_SETS.find((s) => s.output === output);
  if (spec === undefined) {
    throw new Error(`Unknown modeled output: ${output}`);
  }

  const baseMissing = universalBaseMissing(fields);
  if (baseMissing.length > 0) {
    return {
      output,
      ready: false,
      methodId: null,
      labelKey: null,
      usedFields: [],
      missingFields: baseMissing,
      sourceRowIds: [],
    };
  }

  // F55: a per-loan-magnitude output's options are satisfied by the loan's OWN
  // fields only, so method attribution mirrors the derive's per-loan precedence
  // (own net → A, own gross → B). Other outputs keep signal satisfaction.
  const ownFieldOnly = spec.perLoanMagnitude === true;
  const winner = spec.options.find((option) =>
    optionSatisfied(fields, option, signals, vocabulary, ownFieldOnly),
  );
  if (winner === undefined || winner.signalOnly === true) {
    // F55 membership-by-signal: for a per-loan-magnitude output, a loan with NO
    // own loss records — and no own event-only signal either — in an org whose
    // data carries a magnitude feed (any non-signal-only option satisfiable via
    // the portfolio-level signal) is a PERFORMING / no-loss observation: the
    // derive keeps it in the loss denominator as a survivor, so it is READY with
    // NO method (`methodId: null` — it uses no per-loan method, and the method
    // shares/upgrades never count it). A loan whose own event IS named
    // (signal-only winner: a classified flag/status with no amount) stays
    // honestly excluded — the derive's lossEligible=false twin.
    if (ownFieldOnly && winner === undefined) {
      const orgFeed = spec.options.some(
        (option) =>
          option.signalOnly !== true && optionSatisfied(fields, option, signals, vocabulary),
      );
      if (orgFeed) {
        return {
          output,
          ready: true,
          methodId: null,
          labelKey: null,
          usedFields: [],
          missingFields: [],
          sourceRowIds: [],
        };
      }
    }
    // Not ready: no option satisfied, or only a SIGNAL-ONLY option is (a bare
    // charge-off flag/status with no amount — the event is named but no loss
    // magnitude is derivable, so the loan stays honestly excluded). The missing
    // fields point only at options that can actually grant readiness; for the
    // signal-only case that is the cheapest magnitude-bearing path.
    const missing =
      winner === undefined
        ? uniqueKeys(
          spec.options
            .filter((option) => option.signalOnly !== true)
            .flatMap((option) =>
              fieldMissingOrWrong(
                fields,
                optionRequiredKeys(option),
                signals,
                vocabulary,
                ownFieldOnly,
              ),
            ),
        )
        : nearestNeededFields(fields, spec, signals, vocabulary);
    return {
      output,
      ready: false,
      methodId: null,
      labelKey: null,
      usedFields: [],
      missingFields: missing,
      sourceRowIds: [],
    };
  }

  // One satisfying field per clause (the canonical first-present), which is what the
  // source rows are keyed to — sourceRowIds keeps its existing clause-only semantics.
  const keysUsed = uniqueKeys(
    winner.clauses.flatMap((clause) => clause.filter((key) => hasField(fields, key)).slice(0, 1)),
  );
  // What the method draws from, for display: the clause fields plus any present
  // preferred-when-available field (used by the model when supplied). Base excluded.
  const usedFields = uniqueKeys([
    ...keysUsed,
    ...(winner.preferredWhenAvailable ?? []).filter((key) => hasField(fields, key)),
  ]);
  const sourceRowIds = keysUsed
    .map((key) => fields.get(key)?.sourceRowId)
    .filter((id): id is string => id !== undefined);
  return {
    output,
    ready: true,
    methodId: winner.id,
    labelKey: winner.labelKey,
    usedFields,
    missingFields: [],
    sourceRowIds,
  };
}

export function evaluateLoanReadiness(
  loan: PortfolioLoanReadinessInput,
  portfolioSignals: PortfolioSignalSet = EMPTY_PORTFOLIO_SIGNALS,
  // F170: defaults to NO live vocabulary — the strictly-conservative posture
  // (an unclassified status/flag key satisfies nothing). Callers with org
  // context pass `dbOrgLiveEventVocabulary`'s result.
  liveEventVocabulary: EventVocabularySet = EMPTY_EVENT_VOCABULARY,
): LoanReadinessResult {
  const fields = fieldMapForLoan(loan);
  // WS-H I2 (R2): an un-ageable loan (no age anchor — see the input flag's doc)
  // is excluded from every age-indexed curve, so NO output may read ready.
  // `origination_date` leads the missing fields (the R2 unlock); any missing
  // universal-base fields follow, so the guidance never claims origination alone
  // suffices when the base is also broken.
  if (loan.unageable === true) {
    const missing = uniqueKeys(['origination_date', ...universalBaseMissing(fields)]);
    return {
      loanId: loan.loanId,
      outputs: MODELED_OUTPUTS.map((output) => ({
        output,
        ready: false,
        methodId: null,
        labelKey: null,
        usedFields: [],
        missingFields: missing,
        sourceRowIds: [],
      })),
    };
  }
  return {
    loanId: loan.loanId,
    outputs: MODELED_OUTPUTS.map((output) =>
      chooseOutputForLoan(output, fields, portfolioSignals, liveEventVocabulary),
    ),
  };
}

/**
 * One weighted readiness group: a representative field-presence input plus how many
 * loans share EXACTLY that field set. The set-based Review path (A3) groups the
 * portfolio-scale per-loan rows by field-set signature in SQL and evaluates each
 * DISTINCT signature once here — so the JS cost scales with distinct field
 * combinations, never with loan count.
 */
export interface WeightedLoanReadinessGroup {
  readonly input: PortfolioLoanReadinessInput;
  readonly count: number;
}

/** The aggregate half of {@link RowLevelReadinessSummary} — no per-loan array. */
export interface ReadinessOutputsSummary {
  readonly totalLoans: number;
  readonly outputs: readonly OutputReadinessSummary[];
}

/** {@link ReadinessOutputsSummary} plus each input group's readiness verdict (parallel to the input order). */
export interface WeightedReadinessSummary extends ReadinessOutputsSummary {
  readonly groupResults: readonly LoanReadinessResult[];
}

/**
 * The ONE readiness-aggregation implementation, weighted: every tally that
 * {@link summarizeRowLevelReadiness} historically took per loan is multiplied by the
 * group's `count`. `summarizeRowLevelReadiness` delegates here with count-1 groups,
 * so the per-loan and the set-based (signature-grouped) paths can never drift.
 */
export function summarizeReadinessGroups(
  groups: readonly WeightedLoanReadinessGroup[],
  portfolioSignals: PortfolioSignalSet = EMPTY_PORTFOLIO_SIGNALS,
  // F170: no vocabulary by default — the conservative posture (see evaluateLoanReadiness).
  liveEventVocabulary: EventVocabularySet = EMPTY_EVENT_VOCABULARY,
): WeightedReadinessSummary {
  const loanFieldMaps = groups.map((group) => ({
    count: group.count,
    unageable: group.input.unageable === true,
    fields: fieldMapForLoan(group.input),
    readiness: evaluateLoanReadiness(group.input, portfolioSignals, liveEventVocabulary),
  }));
  const totalLoans = groups.reduce((sum, group) => sum + group.count, 0);

  const outputs: OutputReadinessSummary[] = MODELING_FIELD_SETS.map((spec) => {
    const outputResults = loanFieldMaps.map((entry) => {
      const result = entry.readiness.outputs.find((o) => o.output === spec.output);
      if (result === undefined) {
        throw new Error(`Missing output readiness: ${spec.output}`);
      }
      return { ...entry, outputResult: result };
    });

    const readyLoans = outputResults.reduce(
      (sum, entry) => sum + (entry.outputResult.ready ? entry.count : 0),
      0,
    );

    // ── Winning methods, with the fields their loans draw from ──────────────
    // Tally per winning method: loan count + per-field loan counts. A field counts
    // for a loan only when THAT loan's method actually drew from it, so a field's
    // count never exceeds the method's loan count and options never double-count.
    const methodTally = new Map<
      string,
      { labelKey: string; loanCount: number; fieldCounts: Map<StandardFieldKey, number> }
    >();
    for (const entry of outputResults) {
      const { methodId, labelKey, usedFields } = entry.outputResult;
      if (methodId === null || labelKey === null) continue;
      const tally = methodTally.get(methodId) ?? {
        labelKey,
        loanCount: 0,
        fieldCounts: new Map<StandardFieldKey, number>(),
      };
      tally.loanCount += entry.count;
      for (const key of usedFields) {
        tally.fieldCounts.set(key, (tally.fieldCounts.get(key) ?? 0) + entry.count);
      }
      methodTally.set(methodId, tally);
    }
    // Emit in preference order (preferred first), fields in each option's declared order.
    const methods: MethodUsageSummary[] = [];
    for (const option of spec.options) {
      const tally = methodTally.get(option.id);
      if (tally === undefined) continue;
      const declaredOrder = uniqueKeys([
        ...option.clauses.flat(),
        ...(option.preferredWhenAvailable ?? []),
      ]);
      methods.push({
        optionId: option.id,
        labelKey: tally.labelKey,
        loanCount: tally.loanCount,
        percent: percent(tally.loanCount, totalLoans),
        fields: declaredOrder
          .filter((key) => tally.fieldCounts.has(key))
          .map((key) => ({ key, loanCount: tally.fieldCounts.get(key) ?? 0 })),
      });
    }

    // ── Upgrade nudge toward the preferred method ──────────────────────────
    // Loans already ready on a LOWER method that are a small, common field-add away
    // from the preferred one. Group by add-set; surface the single highest-impact group.
    const upgrades: MethodUpgradeSummary[] = [];
    const preferred = spec.options[0];
    if (preferred?.upgradeBenefitKey !== undefined) {
      const addSets = new Map<string, { keys: readonly StandardFieldKey[]; loanCount: number }>();
      for (const entry of outputResults) {
        const result = entry.outputResult;
        if (!result.ready || result.methodId === preferred.id) continue;
        // F55: a methodId-null ready loan is a performing/no-loss observation
        // (membership by signal) — it reports no losses, so nudging it toward a
        // loss-measurement method would be false guidance.
        if (result.methodId === null) continue;
        // F55: for a per-loan-magnitude output the upgrade path is OWN-field —
        // an org-level net signal must not suppress the "add net amounts" nudge
        // for loans the derive books via gross−recovery.
        const add = missingToReach(
          entry.fields,
          preferred,
          portfolioSignals,
          liveEventVocabulary,
          spec.perLoanMagnitude === true,
        );
        if (add.length === 0 || add.length > UPGRADE_MAX_ADD_FIELDS) continue;
        const sig = add.join('|');
        const group = addSets.get(sig);
        addSets.set(sig, { keys: add, loanCount: (group?.loanCount ?? 0) + entry.count });
      }
      const best = [...addSets.values()].sort(
        (a, b) => b.loanCount - a.loanCount || a.keys.join('|').localeCompare(b.keys.join('|')),
      )[0];
      if (best !== undefined) {
        upgrades.push({
          toOptionId: preferred.id,
          toLabelKey: preferred.labelKey,
          benefitKey: preferred.upgradeBenefitKey,
          addFields: best.keys,
          loanCount: best.loanCount,
        });
      }
    }

    // ── Unready remainder + what would unlock the most of it ───────────────
    const unready = outputResults.filter((entry) => !entry.outputResult.ready);
    let remainder: ReadinessRemainder | null = null;
    if (unready.length > 0) {
      const needCounts = new Map<StandardFieldKey, number>();
      for (const entry of unready) {
        // R2: an un-ageable loan's cheapest path is its origination anchor (the
        // result's missingFields already lead with origination_date) — the
        // structural nearest-option walk would misname base/term fields instead.
        const needed = entry.unageable
          ? entry.outputResult.missingFields
          : nearestNeededFields(entry.fields, spec, portfolioSignals, liveEventVocabulary);
        for (const key of needed) {
          needCounts.set(key, (needCounts.get(key) ?? 0) + entry.count);
        }
      }
      const neededFields: FieldUsageSummary[] = [...needCounts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([key, loanCount]) => ({ key, loanCount }));
      remainder = {
        loanCount: unready.reduce((sum, entry) => sum + entry.count, 0),
        neededFields,
      };
    }

    return {
      output: spec.output,
      totalLoans,
      readyLoans,
      readyPercent: percent(readyLoans, totalLoans),
      missingCriticalLoans: totalLoans - readyLoans,
      methods,
      upgrades,
      remainder,
    };
  });

  return {
    totalLoans,
    outputs,
    groupResults: loanFieldMaps.map((entry) => entry.readiness),
  };
}

export function summarizeRowLevelReadiness(
  loans: readonly PortfolioLoanReadinessInput[],
  portfolioSignals: PortfolioSignalSet = EMPTY_PORTFOLIO_SIGNALS,
  // F170: no vocabulary by default — the conservative posture (see evaluateLoanReadiness).
  liveEventVocabulary: EventVocabularySet = EMPTY_EVENT_VOCABULARY,
): RowLevelReadinessSummary {
  // Count-1 groups make the weighted core arithmetically identical to the historical
  // per-loan tallies; `groupResults` is then exactly the per-loan readiness array.
  const summary = summarizeReadinessGroups(
    loans.map((input) => ({ input, count: 1 })),
    portfolioSignals,
    liveEventVocabulary,
  );
  return {
    totalLoans: summary.totalLoans,
    outputs: summary.outputs,
    loans: summary.groupResults,
  };
}
